import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { isBrowser, isMobile } from 'react-device-detect';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { openModalAction } from '../../../actions/modal';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { DATE_MONTH_DAY_YEAR, DATE_MONTH_DAY_YEAR_WITH_DASH } from '../../../constants';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import { getCapChargeText } from '../../../utils';
import './patient.scss';
import { actions } from './redux/actions';
import { actions as kitActions } from '../../Kits/redux/actions';
import { GET_DEVICES, PATIENT_DETAILS_RESULT } from './redux/constants';
import { PageHeader } from '../../../components/PageHeader';
import { PERMISSIONS, hasPermission } from '../../../utils/userPermissions';
import ListOfTables from '../../../containers/ListOfTables';
import { AUTHORIZE_DEXCOM_RESULT, GET_DEXCOM_CONFIG_RESULT, dexcomActions } from './redux/dexcomActions';
import { DeviceClass, DeviceTypes } from '../../../utils/deviceTypes';
import {
  getSearchParamFromUrl,
  hydrateTableRequestFromUrl,
  saveSearchParamToUrl,
  useGlobalPageSize,
} from '../../../utils/tableUtils';
import Select from '../../../components/Select';
import { getKitOption } from '../../../utils/kitOptions';
import { getProgramDisplayName } from '../../../utils/cmsPrograms';
import { Button, HEADER_BUTTON_DARK_BLUE } from 'components/PageHeader/Button';

//#region exports
export const calculateDeviceId = device => {
  if (!device?.deviceId) {
    return '-';
  }
  return device.deviceId.replace(`${device.manufacturer}.`, '').replace(`${device.model}.`, '');
};

export const navigateToDevice = (device, url, patientId, onNavigate) => {
  const deviceType = device.deviceType;
  const deviceId = device.deviceId;
  const capId = device.cap_id;
  let model = device.model;
  let manufacturer = device.manufacturer;
  if (model == '') {
    if (manufacturer.startsWith('gg-rpm')) {
      model = manufacturer.replace('gg-rpm ', '');
      manufacturer = 'gg-rpm';
    }
  }
  const lastActivity = device.lastEventDate ? moment(device.lastEventDate).unix() : moment().unix();

  if (deviceId) {
    if (
      DeviceTypes[deviceType].deviceClass === DeviceClass.hub ||
      DeviceTypes[deviceType].deviceClass === DeviceClass.cap ||
      DeviceTypes[deviceType].deviceClass === DeviceClass.ble
    ) {
      onNavigate(`${url}/${DeviceTypes[deviceType].id}/${deviceId}`);
    } else if (DeviceTypes[deviceType].deviceClass === DeviceClass.thirdParty) {
      onNavigate(
        `${url}/3rdParty/${deviceId}/${manufacturer}/${model}/${encodeURIComponent(DeviceTypes[deviceType].id)}/details?lastActivity=${lastActivity}&patientId=${patientId}`,
      );
    } else {
      onNavigate('/device-under-construction');
    }
  } else {
    onNavigate(`${url}/${capId}/${deviceType}/details`);
  }
};

//#endregion

function PatientDevices(props) {
  const { patientDetails, userOrgId } = props;
  const globalPageSize = useGlobalPageSize();
  const searchTime = React.useRef();
  const hasLoadedOnceRef = React.useRef(false);
  const [searchText, setSearchText] = React.useState(getSearchParamFromUrl() || '');
  const pageRequest = React.useRef({
    offset: 0,
    search: getSearchParamFromUrl(),
    limit: globalPageSize,
    status: 'active',
  });
  const canEdit = hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_DEVICES_EDIT);
  const patientId = props.patientId || props.userId;
  const orgId = patientDetails?.orgId || userOrgId || null;
  const [kitFilter, setKitFilter] = React.useState('');
  const [enrollmentFilter, setEnrollmentFilter] = React.useState('');
  const patientKitIds = Array.from(
    new Set(
      [
        ...(props.kits || []).map(kit => kit.id),
        ...Object.values(patientDetails?.patientEnrollment?.enrollments || {}).map(
          enrollment => enrollment.kitUnguessableId,
        ),
      ].filter(Boolean),
    ),
  );
  const patientKits = patientKitIds.map(kitId => props.kits?.find(kit => kit.id === kitId) || { id: kitId });
  const patientKitOptions = patientKits.map(kit => getKitOption(kit, patientDetails?.patientEnrollment?.enrollments));
  const patientKitFilterOptions = patientKits.map(kit =>
    getKitOption(kit, patientDetails?.patientEnrollment?.enrollments, { usageFirst: true }),
  );
  const enrollmentOptions = Object.entries(patientDetails?.patientEnrollment?.enrollments || {})
    .filter(([, enrollment]) => enrollment.kitUnguessableId)
    .map(([enrollmentId, enrollment]) => ({
      label: `${getProgramDisplayName(enrollment.program, enrollment.subprogram) || enrollment.program} - ${enrollment.status}`,
      value: enrollmentId,
    }));

  useEffect(() => {
    if (!hasLoadedOnceRef.current) {
      hydrateTableRequestFromUrl(pageRequest.current, globalPageSize);
      hasLoadedOnceRef.current = true;
    } else {
      pageRequest.current.offset = 0;
    }
    delete pageRequest.current.kitId;
    delete pageRequest.current.enrollmentId;
    setKitFilter('');
    setEnrollmentFilter('');
    if (patientId !== props.patientId) {
      if (props.dexcomCode) {
        const [dexcomPatientId, dexcomKitId] = (props.dexcomPatient || '').split(':');
        if (dexcomPatientId !== patientId.toString()) {
          showDexcomInvalidPatient();
        } else {
          props.authorizeDexcom({ code: props.dexcomCode, patientId, kitId: dexcomKitId || undefined }).then(resp => {
            if (resp.type === AUTHORIZE_DEXCOM_RESULT) {
              props.getDevices(pageRequest.current, patientId);
              showDexcomDeviceConfigured();
            } else {
              showDexcomConfigurationFailed();
            }
          });
        }
      } else {
        props.getPatientInfo(patientId).then(resp => {
          if (resp.type === PATIENT_DETAILS_RESULT) {
            if (
              (resp?.response?.dexcomStatus === 'new' || resp?.response?.dexcomStatus === 'revoked') &&
              resp.response?.kitIds?.length > 0
            ) {
              props.getDexcomConfig().then(resp1 => {
                if (resp1.type === GET_DEXCOM_CONFIG_RESULT) {
                  showDexcomInvite(resp1.response, resp?.response?.dexcomStatus, resp.response.kitIds);
                }
              });
            }
          }
        });
      }
    }
    props.getDevices(pageRequest.current, patientId);
    if (!props.kits?.length) {
      props.getPatientKitInfo(patientId, orgId);
    }
    return () => {
      props.clearDevice();
    };
  }, [patientId]);

  const showDexcomDeviceConfigured = () => {
    const data = {
      title: <span>{Strings.dexcomDeviceConfiguredSuccess}</span>,
      caption: Strings.dexcomDeviceConfiguration,
      onCancelAction: null,
      hideCaution: true,
      noButtonText: Strings.ok,
      hideYesButton: true,
    };
    props.openConfirmModal(data);
  };

  const showDexcomConfigurationFailed = () => {
    const data = {
      title: <span>{Strings.dexcomConfigurationFailedText}</span>,
      caption: Strings.dexcomConfigurationFailed,
      onCancelAction: null,
      hideCaution: true,
      noButtonText: Strings.ok,
      hideYesButton: true,
    };
    props.openConfirmModal(data);
  };

  const showDexcomInvalidPatient = () => {
    const data = {
      title: <span>{Strings.dexcomCodeNotMatchingPatient}</span>,
      caption: Strings.dexcomInvalidCode,
      onCancelAction: null,
      hideCaution: true,
      noButtonText: Strings.ok,
      hideYesButton: true,
    };
    props.openConfirmModal(data);
  };

  const showDexcomInvite = (config, status, kitIds) => {
    const { client_id, host, redirect_uri } = config;
    let selectedKitId = kitIds.length === 1 ? kitIds[0] : '';
    const data = {
      title: (
        <>
          <span>{status === 'new' ? Strings.authorizeRxcapForDexcom : Strings.reAuthorizeRxcapForDexcom}</span>
          {kitIds.length > 1 && (
            <Select
              placeholder={Strings.selectDexcomKit}
              data={kitIds.map(kitId => ({ label: kitId, value: kitId }))}
              onChange={option => { selectedKitId = option.value; }}
              isSearchable
            />
          )}
        </>
      ),
      caption: Strings.dexcomDeviceConfiguration,
      onConfirmAction: () => {
        if (selectedKitId) {
          window.location = `${host}/v2/oauth2/login?client_id=${client_id}&redirect_uri=${redirect_uri}&response_type=code&scope=offline_access&state=${patientId}:${selectedKitId}`;
        }
      },
      onCancelAction: null,
      hideCaution: true,
      yesButtonText: Strings.ok,
      noButtonText: Strings.notNow,
    };
    props.openConfirmModal(data);
  };

  const onSearchQueryChange = query => {
    setSearchText(query);
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    saveSearchParamToUrl(query);
    clearTimeout(searchTime.current);
    searchTime.current = setTimeout(() => {
      props.getDevices(pageRequest.current, patientId);
    }, 1000);
  };

  const onOffsetChange = (offset, limit) => {
    pageRequest.current.offset = offset;
    if (limit) {
      pageRequest.current.limit = limit;
    }
    props.getDevices(pageRequest.current, patientId);
  };

  const onPageSizeChange = limit => {
    pageRequest.current.offset = 0;
    pageRequest.current.limit = limit;
    props.getDevices(pageRequest.current, patientId);
  };

  const onSortingChange = sorting => {
    const sortColumn = sorting?.length > 0 ? sorting[0].id : undefined;
    const sortType = sorting?.length > 0 ? (sorting[0].desc ? 'descending' : 'ascending') : undefined;

    if (sortColumn === pageRequest.current.sortColumn && sortType === pageRequest.current.sortType) {
      return;
    }

    pageRequest.current.offset = 0;
    pageRequest.current.sortColumn = sortColumn;
    pageRequest.current.sortType = sortType;

    props.getDevices(pageRequest.current, patientId);
  };

  const onFilterChange = (filterName, value, setFilter) => {
    pageRequest.current.offset = 0;
    if (value) {
      pageRequest.current[filterName] = value;
    } else {
      delete pageRequest.current[filterName];
    }
    setFilter(value);
    props.getDevices(pageRequest.current, patientId);
  };

  const onDeviceDelete = (_, device) => {
    const data = {
      title: (
        <span>
          {Strings.deleteHubDeviceWarning} <b>{device.cap_name || device.cap_id}</b>?
        </span>
      ),

      onConfirmAction: kitActions.unassignDeviceFromKit(device.kitId, device.cap_id, [
        actions.getDevices(pageRequest.current, props.patientId),
      ]),
      onCancelAction: null,
    };
    props.openConfirmModal(data);
  };

  const PatientHeader = React.useMemo(() => {
    const { isLoading, pagination, patientDevices } = props;
    const headerTitle =
      !isLoading && patientDevices && pagination
        ? Strings.formatString(
            Strings.showingXDevicesAndKits,
            patientDevices.length,
            pagination.totalRecords ? pagination.totalRecords : 0,
            props.kits?.length || 0,
          )
        : Strings.showingWait;
    return (
      <>
        <PageHeader
          isBlack
          left={headerTitle}
          right={(
            <>
              {canEdit && (
                <Button class={HEADER_BUTTON_DARK_BLUE} onClick={() => props.onAttachKit(pageRequest.current, orgId)} title={Strings.attachKit} />
              )}
              {props.kits?.length > 0 && canEdit && (
                <Button class={HEADER_BUTTON_DARK_BLUE} onClick={() => props.onReassignKit(pageRequest.current, orgId)} title={Strings.reassignKit} />
              )}
              {props.kits?.length > 0 && canEdit && (
                <Button
                  class={HEADER_BUTTON_DARK_BLUE}
                  onClick={() => props.onAttachCap(pageRequest.current, orgId, patientKitOptions)}
                  title={Strings.attachDevice}
                />
              )}
            </>
          )}
        />
        <PageHeader
          right={(
            <>
              <Select
                id="patient-devices-kit-filter"
                className="patient-devices-filter"
                label={Strings.kit}
                data={[{ label: Strings.allKits, value: '' }, ...patientKitFilterOptions]}
                value={kitFilter}
                onChange={option => onFilterChange('kitId', option?.value || '', setKitFilter)}
                isSearchable
              />
              <Select
                id="patient-devices-enrollment-filter"
                className="patient-devices-filter"
                label={Strings.enrollment}
                data={[{ label: Strings.allEnrollments, value: '' }, ...enrollmentOptions]}
                value={enrollmentFilter}
                onChange={option => onFilterChange('enrollmentId', option?.value || '', setEnrollmentFilter)}
                isSearchable
              />
              <TextInput
                class="search"
                placeholder={Strings.search}
                value={searchText}
                onChange={e => onSearchQueryChange(e.target.value)}
              />
            </>
          )}
        />
      </>
    );
  }, [props.isLoading, props.pagination, props.patientDevices, props.kits, kitFilter, enrollmentFilter]);

  const onRowSelected = device => {
    navigateToDevice(device, `/cap-patients/${patientId}/devices`, patientId, props.onNavigate);
  };

  const renderDeviceId = device => {
    const id = calculateDeviceId(device);
    return id;
  };

  const renderKit = device => {
    if (!device.kitId) {
      return '-';
    }
    return device.kitName ? `${device.kitName} (${device.kitId})` : device.kitId;
  };

  const renderEnrollments = enrollments =>
    (enrollments || [])
      .map(enrollment =>
        `${getProgramDisplayName(enrollment.program, enrollment.subprogram) || enrollment.program} - ${enrollment.status}`,
      )
      .join(', ') || '-';

  const renderEnrollmentIds = enrollments =>
    (enrollments || []).map(enrollment => enrollment.enrollmentId).join(', ') || '-';

  const columns = React.useMemo(() => [
    columnHelper.accessor('cap_id', {
      header: Strings.deviceId,
      cell: info => renderDeviceId(info.row.original),
      size: 170,
      enableSorting: true,
    }),
    columnHelper.accessor('cap_name', {
      header: Strings.deviceModel,
      accessorFn: d => `${d.manufacturer} ${d.model}`,
      size: 220,
    }),
    columnHelper.accessor('deviceType', {
      header: Strings.deviceType,
      size: 160,
    }),
    columnHelper.accessor('kit', {
      header: Strings.kit,
      cell: info => renderKit(info.row.original),
      size: 230,
    }),
    columnHelper.accessor('enrollment', {
      header: Strings.enrollment,
      accessorFn: d => d.enrollments,
      cell: info => renderEnrollments(info.getValue()),
      size: 260,
    }),
    columnHelper.accessor('enrollmentId', {
      header: Strings.enrollmentId,
      accessorFn: d => d.enrollments,
      cell: info => renderEnrollmentIds(info.getValue()),
      size: 220,
    }),
    columnHelper.accessor('lastEventDate', {
      header: Strings.lastEventTime,
      cell: info => info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR_WITH_DASH) : '-',
      size: 190,
    }),
    columnHelper.accessor('registrationDate', {
      header: Strings.capPatient.registeredDate,
      cell: info => info.getValue() ? moment.unix(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-',
      size: 180,
    }),
    columnHelper.accessor('activationDate', {
      header: Strings.capPatient.activationDate,
      cell: info => info.getValue() ? moment.unix(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-',
      size: 180,
    }),
    columnHelper.accessor('battery', {
      header: Strings.schedules.battery,
      cell: info => getCapChargeText(info.getValue()),
      size: 140,
    }),
  ], []);

  const mobileRows = React.useMemo(() => [
    { props: { title: Strings.deviceId, value: d => renderDeviceId(d) } },
    { props: { title: Strings.deviceModel, value: d => `${d.manufacturer} ${d.model}` } },
    { props: { title: Strings.deviceType, value: d => d.deviceType } },
    { props: { title: Strings.kit, value: d => renderKit(d) } },
    { props: { title: Strings.enrollment, value: d => renderEnrollments(d.enrollments) } },
    { props: { title: Strings.enrollmentId, value: d => renderEnrollmentIds(d.enrollments) } },
    {
      props: {
        title: Strings.lastEventTime,
        value: d => (d.lastEventDate ? moment(d.lastEventDate).format(DATE_MONTH_DAY_YEAR_WITH_DASH) : '-'),
      },
    },
    {
      props: {
        title: Strings.capPatient.registeredDate,
        value: d => (d.registrationDate ? moment.unix(d.registrationDate).format(DATE_MONTH_DAY_YEAR) : '-'),
      },
    },
    {
      props: {
        title: Strings.capPatient.activationDate,
        value: d => (d.activationDate ? moment.unix(d.activationDate).format(DATE_MONTH_DAY_YEAR) : '-'),
      },
    },
    { props: { title: Strings.schedules.battery, value: d => getCapChargeText(d.battery) } },
  ], []);

  const { isLoading, pagination, patientDevices } = props;
  const buttons = [
    {
      icon: 'delete',
      onClick: onDeviceDelete,
      disabled: () => {
        return false;
      },
      text: Strings.delete,
    },
  ];

  return (
    <OverlayScrollbarsComponent
      defer
      className="scrollbar-right-margin"
      options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
    >
      {props.patientVersion ? <h2>{Strings.yourDevices}</h2> : PatientHeader}

      {isBrowser && props.kits?.length > 0 && (
        <Table3
          isLoading={isLoading}
          name="patient-devices"
          data={patientDevices || []}
          onRowClick={
            props.patientVersion || !hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_DEVICE_DETAILS)
              ? undefined
              : onRowSelected
          }
          onOffsetChange={onOffsetChange}
          onPageSizeChange={onPageSizeChange}
          onSortingChange={onSortingChange}
          pagination={
            pagination || {
              offset: 0,
              limit: pageRequest.current.limit,
              totalRecords: 0,
            }
          }
          columns={columns}
          enableColumnFiltering
          pinFirstColumn
          buttons={canEdit ? buttons : undefined}
        />
      )}
      {isMobile && props.kits?.length > 0 && (
        <>
          <ListOfTables
            isLoading={isLoading}
            data={patientDevices || []}
            title={row => row.deviceType}
            rows={mobileRows}
          />
        </>
      )}
    </OverlayScrollbarsComponent>
  );
}

PatientDevices.propTypes = {
  clearDevice: PropTypes.func,
  getDevices: PropTypes.func,
  isLoading: PropTypes.any,
  onAttachCap: PropTypes.func,
  onAttachKit: PropTypes.func,
  onReassignKit: PropTypes.func,
  onNavigate: PropTypes.func,
  openConfirmModal: PropTypes.func,
  pagination: PropTypes.shape({
    offset: PropTypes.number,
    limit: PropTypes.number,
    totalRecords: PropTypes.number,
  }),
  patientDevices: PropTypes.array,
  patientId: PropTypes.any,
  schedules: PropTypes.any,
};

const mapStateToProps = state => {
  const { patients } = state.superUser;
  const urlParams = new URLSearchParams(window.location.search);

  return {
    patientDevices: patients && patients?.patientDevices?.data,
    isLoading: patients && patients?.isPatientDevicesLoading,
    pagination: patients && patients?.patientDevices?.pagination,
    userId: state.auth.profile?.id,
    userOrgId: state.auth.organization?.masked_id,
    kits: state.entities.kits?.patientKits,
    dexcomCode: urlParams.get('code'),
    dexcomPatient: urlParams.get('state'),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onNavigate: path => dispatch(push(path)),
  getDevices: (pageRequest, patientId) => dispatch(actions.getDevices(pageRequest, patientId)),
  onAttachCap: (req, orgId, kitOptions) =>
    dispatch(
      openModalAction('attach-cap-to-schedule', {
        action: actions.getDevices,
        actionType: GET_DEVICES,
        caption: Strings.attachDevice,
        request: req, // props.match.params.id,
        patientDevice: true,
        patientId: ownProps.patientId,
        kitOptions,
        orgId,
      }),
    ),
  onAttachKit: (req, orgId) =>
    dispatch(
      openModalAction('attach-kit', {
        action: actions.getDevices,
        patientAction: actions.getPatientInfo,
        actionType: GET_DEVICES,
        caption: Strings.attachKit,
        request: req, // props.match.params.id,
        kits: ownProps.kits,
        enrollments: ownProps.patientDetails?.patientEnrollment?.enrollments,
        patientId: ownProps.patientId,
        orgId,
      }),
    ),
  onReassignKit: (req, orgId) =>
    dispatch(
      openModalAction('attach-kit', {
        action: actions.getDevices,
        patientAction: actions.getPatientInfo,
        actionType: GET_DEVICES,
        caption: Strings.reassignKit,
        request: req,
        kits: ownProps.kits,
        enrollments: ownProps.patientDetails?.patientEnrollment?.enrollments,
        patientId: ownProps.patientId,
        orgId,
        reassign: true,
      }),
    ),
  clearDevice: () => dispatch(actions.clearDevice()),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  getPatientKitInfo: patientId => dispatch(kitActions.getPatientKitInfo(patientId)),
  getDexcomConfig: () => dispatch(dexcomActions.getDexcomConfig()),
  authorizeDexcom: request => dispatch(dexcomActions.authorizeDexcom(request)),
  getPatientInfo: patientId => dispatch(actions.getPatientInfo(patientId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatientDevices);
